/**
 * Service-types step (#1498).
 *
 * `ServiceFormDialog` owns create/update and its own toast. Completion is
 * read from the services query — a fact, not an event — so a revisit where
 * types already exist is counted without waiting for a dialog callback.
 */

import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { useServices } from '@/hooks/useServices';
import { ServiceFormDialog } from '@/features/settings/services/ServiceFormDialog';

interface ServicesStepProps {
  canManage: boolean;
  onServiceCountChange: (count: number) => void;
}

export function ServicesStep({ canManage, onServiceCountChange }: ServicesStepProps) {
  const { t } = useTranslation('onboarding');
  const [dialogOpen, setDialogOpen] = useState(false);
  const { data, isLoading } = useServices({ isActive: true });

  const serviceCount = data?.length ?? 0;
  const hasService = serviceCount > 0;

  const lastReported = useRef<number | null>(null);
  useEffect(() => {
    if (isLoading || lastReported.current === serviceCount) return;
    lastReported.current = serviceCount;
    onServiceCountChange(serviceCount);
  }, [serviceCount, isLoading, onServiceCountChange]);

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('services.intro')}
      </Typography>

      <Stack spacing={2} alignItems="flex-start">
        {canManage ? (
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
            {t('services.add')}
          </Button>
        ) : (
          <Alert severity="info" sx={{ width: '100%' }}>
            {t('services.noPermission')}
          </Alert>
        )}

        {isLoading ? (
          <CircularProgress size={20} />
        ) : hasService ? (
          <Alert severity="success" sx={{ width: '100%' }}>
            {t('services.registered', { count: serviceCount })}
          </Alert>
        ) : (
          <Alert severity="info" sx={{ width: '100%' }}>
            {t('services.none')}
          </Alert>
        )}
      </Stack>

      {dialogOpen && (
        <ServiceFormDialog open onClose={() => setDialogOpen(false)} editingService={null} />
      )}
    </Box>
  );
}
